import { errorMessage } from '@runnerboy/api-client';
import { Alert, Badge, Button, Card, cx, dateTime, Field, humanize, Input, inr, PageHeader, Sheet, Skeleton, Stat, Switch, Textarea, useToast } from '@runnerboy/ui';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { client, useStoreSession } from './session';

interface InvRow {
  storeProductId: string;
  name: string;
  brand: string;
  category: string;
  productType: string;
  unitLabel: string;
  sku: string;
  mrpMinor: number;
  priceMinor: number;
  isListed: boolean;
  onHand: number;
  reserved: number;
  available: number;
  isAvailable: boolean;
  lowStock: boolean;
}

export function InventoryPage() {
  const { t } = useTranslation();
  const { store } = useStoreSession();
  const toast = useToast();
  const [rows, setRows] = useState<InvRow[] | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [bulkOpen, setBulkOpen] = useState(false);
  const load = useCallback(async () => {
    if (store) setRows(await client.get<InvRow[]>(`/store/stores/${store.id}/inventory`));
  }, [store]);
  useEffect(() => {
    void load();
  }, [load]);
  const update = async (r: InvRow, patch: Record<string, unknown>) => {
    try {
      await client.patch(`/store/stores/${store?.id}/inventory/${r.storeProductId}`, patch);
      await load();
    } catch (e) {
      toast('danger', errorMessage(e));
    }
  };
  const visible = useMemo(
    () =>
      (rows ?? []).filter((r) => (!q || `${r.name} ${r.brand} ${r.sku}`.toLowerCase().includes(q.toLowerCase())) && (filter === 'all' || (filter === 'low' ? r.lowStock : !r.isAvailable || r.available === 0))),
    [rows, q, filter],
  );
  return (
    <div>
      <PageHeader title={t('store.inventory', 'Inventory & prices')} subtitle={t('store.inventorySub', 'Changes apply to the live catalog immediately.')} actions={<Button variant="outline" onClick={() => setBulkOpen(true)}>{t('store.bulk', 'Bulk update')}</Button>} />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-60 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('store.searchInventory', 'Search product, brand or SKU')} className="h-11 pl-9" aria-label="Search inventory" />
        </div>
        {(['all', 'low', 'out'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={cx('h-11 rounded-full px-4 text-sm font-semibold ring-1', filter === f ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white ring-ink-200')}>
            {f === 'all' ? t('store.all', 'All') : f === 'low' ? t('store.low', 'Low stock') : t('store.out', 'Out of stock')}
          </button>
        ))}
      </div>
      <Card className="mt-4 overflow-x-auto">
        {!rows ? (
          <Skeleton className="m-4 h-64" />
        ) : (
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-3">{t('store.product', 'Product')}</th>
                <th className="px-3 py-3">MRP</th>
                <th className="px-3 py-3">{t('store.price', 'Price')}</th>
                <th className="px-3 py-3">{t('store.stock', 'On hand')}</th>
                <th className="px-3 py-3">{t('store.reserved', 'Reserved')}</th>
                <th className="px-3 py-3">{t('store.available', 'Available')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {visible.map((r) => (
                <InventoryRow key={r.storeProductId} r={r} onUpdate={(p) => void update(r, p)} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <BulkSheet open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={() => void load()} />
    </div>
  );
}

function InventoryRow({ r, onUpdate }: { r: InvRow; onUpdate: (patch: Record<string, unknown>) => void }) {
  const [price, setPrice] = useState(String(r.priceMinor / 100));
  const [stock, setStock] = useState(String(r.onHand));
  useEffect(() => {
    setPrice(String(r.priceMinor / 100));
    setStock(String(r.onHand));
  }, [r.priceMinor, r.onHand]);
  return (
    <tr className={cx(!r.isAvailable && 'bg-ink-50/70 text-ink-500')}>
      <td className="px-4 py-2.5">
        <div className="font-semibold text-ink-900">{r.name}</div>
        <div className="text-xs text-ink-500">
          {r.unitLabel} · {r.sku} {r.productType === 'ALCOHOL' && <Badge tone="dark">21+</Badge>} {r.lowStock && r.isAvailable && <Badge tone="warning">Low</Badge>}
        </div>
      </td>
      <td className="rb-tabular px-3">{inr(r.mrpMinor)}</td>
      <td className="px-3">
        <Input
          aria-label={`Price for ${r.name}`}
          value={price}
          inputMode="decimal"
          onChange={(e) => setPrice(e.target.value)}
          onBlur={() => Number(price) * 100 !== r.priceMinor && onUpdate({ priceMinor: Math.round(Number(price) * 100) })}
          className="h-9 w-24 text-sm"
        />
      </td>
      <td className="px-3">
        <div className="flex items-center gap-1">
          <button className="h-9 w-9 rounded-lg bg-ink-100 font-bold" aria-label={`Decrease stock of ${r.name}`} onClick={() => onUpdate({ delta: -1, reason: 'Quick adjust' })}>
            −
          </button>
          <Input aria-label={`Stock for ${r.name}`} value={stock} inputMode="numeric" onChange={(e) => setStock(e.target.value.replace(/\D/g, ''))} onBlur={() => Number(stock) !== r.onHand && onUpdate({ setOnHand: Number(stock), reason: 'Stock count' })} className="h-9 w-16 text-center text-sm" />
          <button className="h-9 w-9 rounded-lg bg-ink-100 font-bold" aria-label={`Increase stock of ${r.name}`} onClick={() => onUpdate({ delta: 1, reason: 'Quick adjust' })}>
            +
          </button>
        </div>
      </td>
      <td className="rb-tabular px-3">{r.reserved}</td>
      <td className="px-3">
        <div className="flex items-center gap-2">
          <Switch label={`${r.name} available`} checked={r.isAvailable} onChange={(v) => onUpdate({ isAvailable: v, reason: v ? 'Back in stock' : 'Marked out of stock' })} />
          <span className="rb-tabular text-xs">{r.available}</span>
        </div>
      </td>
    </tr>
  );
}

function BulkSheet({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { store } = useStoreSession();
  const [text, setText] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const toast = useToast();
  const submit = async () => {
    const rows = text
      .split(/\r?\n/)
      .map((l) => l.split(',').map((x) => x.trim()))
      .filter((c) => c[0])
      .map(([sku, onHand, price]) => ({ sku: sku as string, ...(onHand ? { onHand: Number(onHand) } : {}), ...(price ? { priceMinor: Math.round(Number(price) * 100) } : {}) }));
    try {
      const r = await client.post<{ updated: number; results: { sku: string; ok: boolean; error?: string }[] }>(`/store/stores/${store?.id}/inventory/bulk`, { rows });
      setResult(`${r.updated}/${rows.length} updated. ${r.results.filter((x) => !x.ok).map((x) => `${x.sku}: ${x.error}`).join('; ')}`);
      onDone();
    } catch (e) {
      toast('danger', errorMessage(e));
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t('store.bulk', 'Bulk update')}>
      <p className="text-sm text-ink-600">{t('store.bulkBody', 'One product per line: SKU, stock on hand, price in ₹ (price optional). Price can never exceed MRP.')}</p>
      <Textarea className="mt-3 font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} placeholder={'DEMO-0001, 48, 185\nDEMO-0030, 120'} rows={8} />
      {result && <Alert tone="info" className="mt-3">{result}</Alert>}
      <Button block className="mt-4" onClick={() => void submit()} disabled={!text.trim()}>
        {t('store.apply', 'Apply')}
      </Button>
    </Sheet>
  );
}

interface Profile {
  id: string;
  name: string;
  legalEntityName: string;
  gstin: string | null;
  status: string;
  operationalState: 'OPEN' | 'PAUSED' | 'CLOSED';
  address: string;
  avgPrepMinutes: number;
  isDemo: boolean;
  hours: { dayOfWeek: number; opens: string; closes: string }[];
  licences: { id: string; kind: string; licenceNumber: string; issuingAuthority: string; validTo: string | null; status: string; permitsDelivery: boolean; isDemo: boolean }[];
  staff: { id: string; name: string | null; phone: string | null; role: string; active: boolean }[];
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function StoreProfilePage() {
  const { t } = useTranslation();
  const { store, reloadStores } = useStoreSession();
  const toast = useToast();
  const [p, setP] = useState<Profile | null>(null);
  const load = useCallback(async () => {
    if (store) setP(await client.get<Profile>(`/store/stores/${store.id}/profile`));
  }, [store]);
  useEffect(() => {
    void load();
  }, [load]);
  const setState = async (state: 'OPEN' | 'PAUSED' | 'CLOSED') => {
    try {
      await client.put(`/store/stores/${store?.id}/state`, { state });
      await Promise.all([load(), reloadStores()]);
      toast('success', `Store ${state.toLowerCase()}`);
    } catch (e) {
      toast('danger', errorMessage(e));
    }
  };
  if (!p) return <Skeleton className="h-96" />;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={p.name} subtitle={`${p.legalEntityName} · ${p.address}`} />
      {p.isDemo && <Alert tone="warning">DEMO STORE — licences shown are NOT real licences.</Alert>}
      <Card className="p-4">
        <div className="text-sm font-bold">{t('store.availability', 'Store availability')}</div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {(['OPEN', 'PAUSED', 'CLOSED'] as const).map((s) => (
            <Button key={s} variant={p.operationalState === s ? (s === 'OPEN' ? 'primary' : s === 'PAUSED' ? 'accent' : 'danger') : 'outline'} onClick={() => void setState(s)}>
              {humanize(s)}
            </Button>
          ))}
        </div>
        <p className="mt-2 text-xs text-ink-500">{t('store.pauseHint', 'Pause to stop new orders temporarily (e.g. rush or stock count). Open orders continue.')}</p>
      </Card>
      <Card className="p-4">
        <div className="text-sm font-bold">{t('store.licences', 'Licences & documents')}</div>
        <div className="mt-3 flex flex-col gap-2">
          {p.licences.map((l) => {
            const days = l.validTo ? Math.ceil((Date.parse(l.validTo) - Date.now()) / 86_400_000) : null;
            return (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 rounded-[12px] bg-ink-50 p-3 text-sm">
                <div>
                  <div className="font-semibold">{humanize(l.kind)}</div>
                  <div className="text-xs text-ink-500">
                    {l.licenceNumber} · {l.issuingAuthority}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone={l.status === 'VERIFIED' ? 'success' : l.status === 'EXPIRED' ? 'danger' : 'warning'}>{l.status}</Badge>
                  {days !== null && <Badge tone={days < 30 ? 'danger' : 'neutral'}>{days > 0 ? `${days} days left` : 'Expired'}</Badge>}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-ink-500">{t('store.licenceHint', 'Licence changes are reviewed by Runner Boy compliance. Expired excise licences automatically suspend alcohol orders.')}</p>
      </Card>
      <Card className="p-4">
        <div className="text-sm font-bold">{t('store.hours', 'Opening hours')}</div>
        <div className="mt-2 grid grid-cols-2 gap-1 text-sm sm:grid-cols-4">
          {p.hours.map((h) => (
            <div key={h.dayOfWeek} className="flex justify-between gap-2 rounded-lg bg-ink-50 px-3 py-2">
              <span className="font-semibold">{DAYS[h.dayOfWeek]}</span>
              <span className="rb-tabular">{h.opens === h.closes ? '24h' : `${h.opens}–${h.closes}`}</span>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4">
        <div className="text-sm font-bold">{t('store.staff', 'Staff')}</div>
        {p.staff.map((s) => (
          <div key={s.id} className="mt-2 flex justify-between text-sm">
            <span>
              {s.name ?? '—'} <span className="text-ink-500">{s.phone}</span>
            </span>
            <Badge>{humanize(s.role)}</Badge>
          </div>
        ))}
      </Card>
    </div>
  );
}

export function SettlementsPage() {
  const { store } = useStoreSession();
  const [data, setData] = useState<{ totals: { gross: number; net: number; pending: number }; entries: { id: string; grossItemsMinor: number; commissionMinor: number; processingMinor: number; gstOnChargesMinor: number; netPayableMinor: number; status: string; createdAt: string; order: { code: string } }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (store) client.get<typeof data>(`/store/stores/${store.id}/settlements`).then(setData).catch((e) => setError(errorMessage(e)));
  }, [store]);
  if (error) return <Alert tone="warning">{error}</Alert>;
  if (!data) return <Skeleton className="h-64" />;
  return (
    <div>
      <PageHeader title="Settlements" subtitle="As seller of record you receive item value less commission, processing and GST on charges." />
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Card className="p-4"><Stat label="Gross items" value={inr(data.totals.gross)} /></Card>
        <Card className="p-4"><Stat label="Net payable" value={inr(data.totals.net)} /></Card>
        <Card className="p-4"><Stat label="Pending payout" value={inr(data.totals.pending)} /></Card>
      </div>
      <Card className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-ink-50 text-left text-xs uppercase text-ink-500">
            <tr>
              <th className="px-4 py-3">Order</th>
              <th className="px-3">Gross</th>
              <th className="px-3">Commission</th>
              <th className="px-3">Processing</th>
              <th className="px-3">GST</th>
              <th className="px-3">Net</th>
              <th className="px-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {data.entries.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-2.5 font-semibold">{e.order.code}<div className="text-xs font-normal text-ink-500">{dateTime(e.createdAt)}</div></td>
                <td className="rb-tabular px-3">{inr(e.grossItemsMinor)}</td>
                <td className="rb-tabular px-3">−{inr(e.commissionMinor)}</td>
                <td className="rb-tabular px-3">−{inr(e.processingMinor)}</td>
                <td className="rb-tabular px-3">−{inr(e.gstOnChargesMinor)}</td>
                <td className="rb-tabular px-3 font-bold">{inr(e.netPayableMinor)}</td>
                <td className="px-3"><Badge tone={e.status === 'PAID' ? 'success' : 'warning'}>{e.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function AnalyticsPage() {
  const { store } = useStoreSession();
  const [d, setD] = useState<{ orders: number; delivered: number; rejectedOrCancelled: number; gmvMinor: number; avgAcceptSeconds: number | null; fillRate: number | null } | null>(null);
  useEffect(() => {
    if (store) void client.get<typeof d>(`/store/stores/${store.id}/analytics`).then(setD);
  }, [store]);
  if (!d) return <Skeleton className="h-40" />;
  return (
    <div>
      <PageHeader title="Last 7 days" />
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Card className="p-4"><Stat label="Orders" value={d.orders} /></Card>
        <Card className="p-4"><Stat label="Delivered" value={d.delivered} /></Card>
        <Card className="p-4"><Stat label="Rejected / cancelled" value={d.rejectedOrCancelled} /></Card>
        <Card className="p-4"><Stat label="GMV delivered" value={inr(d.gmvMinor)} /></Card>
        <Card className="p-4"><Stat label="Avg accept time" value={d.avgAcceptSeconds !== null ? `${d.avgAcceptSeconds}s` : '—'} /></Card>
        <Card className="p-4"><Stat label="Fill rate" value={d.fillRate !== null ? `${Math.round(d.fillRate * 100)}%` : '—'} /></Card>
      </div>
    </div>
  );
}

export function StoreSupportPage() {
  const toast = useToast();
  const [form, setForm] = useState({ subject: '', message: '' });
  return (
    <div>
      <PageHeader title="Support" subtitle="Reach Runner Boy partner operations." />
      <Card className="mt-4 flex flex-col gap-3 p-4">
        <Field label="Subject" htmlFor="s">
          <Input id="s" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
        </Field>
        <Field label="Message" htmlFor="m">
          <Textarea id="m" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
        </Field>
        <Button
          disabled={form.subject.length < 3 || form.message.length < 3}
          onClick={async () => {
            try {
              const r = await client.post<{ code: string }>('/support/tickets', { category: 'OTHER', ...form });
              toast('success', `Ticket ${r.code} created`);
              setForm({ subject: '', message: '' });
            } catch (e) {
              toast('danger', errorMessage(e));
            }
          }}
        >
          Send
        </Button>
      </Card>
    </div>
  );
}
